/**
 * The vocabulary every interaction answer is described in, plus the primitives the
 * per-type formatters share.
 *
 * Interaction field values are as varied as the field types that produce them: a text
 * field holds a string, an allocation field holds a whole location node, an upload
 * holds a file reference, and a signature holds an entire `data:image/png;base64,…`
 * string. Printing them all with `String(value)` — or worse, `JSON.stringify` — is
 * what put raw JSON and walls of base64 into the job history in the first place. So
 * every value is classified before it is rendered, and the shapes that must never be
 * printed as text get their own result kinds.
 */

/** A signature value that must render as an image, never as its string. */
export interface InteractionImageValue {
  kind: 'image';
  /** A `data:` URI or an http(s) URL. Safe to use as an `<img src>`. */
  src: string;
  alt: string;
}

/** Text safe to render directly. */
export interface InteractionTextValue {
  kind: 'text';
  text: string;
}

/**
 * An uploaded file — a photo, an attachment, a merchant damage template.
 *
 * Unlike a signature, the answer holds no image data: it holds a *reference*, and the
 * two surfaces that collect uploads reference it differently. Mobile stores the
 * descriptor the upload API returned (`{fileName, fileKey, fileId}`); desktop stores
 * only the sentence "<name> successfully uploaded". So `fileKey` is optional, and a
 * consumer that wants to show the file has to resolve it — by key when there is one,
 * by name against the job's attachments when there isn't.
 */
export interface InteractionFileValue {
  kind: 'file';
  /** The file name as uploaded, e.g. `damage-front.jpg`. */
  fileName: string;
  /** Storage key, present only when the answer carried the full descriptor. */
  fileKey?: string;
}

/** The field contributes nothing to the log (structural, or empty). */
export interface InteractionOmittedValue {
  kind: 'omitted';
}

export type InteractionDisplayValue =
  | InteractionImageValue
  | InteractionFileValue
  | InteractionTextValue
  | InteractionOmittedValue;

export const OMITTED: InteractionOmittedValue = { kind: 'omitted' };

/**
 * How a display value reads when only text is available — a plain-prose note, or one
 * entry of a multi-value answer. Returns undefined for the kinds that have no textual
 * form worth printing (an image, and anything omitted).
 */
export const displayValueText = (value: InteractionDisplayValue): string | undefined => {
  if (value.kind === 'text') return value.text;
  if (value.kind === 'file') return value.fileName;
  return undefined;
};

/**
 * A data URI holding an image, which is what a signature pad produces.
 *
 * SVG is excluded to match `note-text-tidy.ts`: note and answer content is
 * user-authored, and a signature pad has no reason to emit one, so it is shown as
 * text rather than promoted into an element.
 */
export const IMAGE_DATA_URI = /^data:image\/(?!svg)[a-z0-9.+-]+;base64,/i;

/**
 * The keys worth showing when an object turns up that has no dedicated formatter.
 * Checked in order, so the most name-like wins. This is the safety net that keeps
 * `JSON.stringify` out of the UI.
 */
const LABEL_KEYS = ['name', 'title', 'label', 'fileName', 'partName', 'code'] as const;

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const text = (value: string): InteractionDisplayValue =>
  value.trim() === '' ? OMITTED : { kind: 'text', text: value };

/**
 * Best-effort label for an object with no dedicated formatter: the first recognisable
 * name-ish key, else the count of properties. Never the raw JSON.
 */
export const summariseObject = (value: Record<string, unknown>): InteractionDisplayValue => {
  for (const key of LABEL_KEYS) {
    const candidate = value[key];
    if (typeof candidate === 'string' && candidate.trim() !== '') return text(candidate);
    if (typeof candidate === 'number') return text(String(candidate));
  }
  const count = Object.keys(value).length;
  if (count === 0) return OMITTED;
  return { kind: 'text', text: `(${count} ${count === 1 ? 'value' : 'values'})` };
};
